import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  ArrowLeft, 
  TrendingUp, 
  Trophy, 
  Target, 
  Users,
  Zap,
  Award,
  BarChart3
} from "lucide-react";
import { useLocation } from "wouter";

export function HowItWorks() {
  const [, setLocation] = useLocation();

  return (
    <div className="min-h-screen bg-background">
      <header 
        className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b"
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        <div className="container max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => setLocation("/")}
            data-testid="button-back"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-lg font-semibold">Cómo Funciona</h1>
        </div>
      </header>

      <main className="container max-w-4xl mx-auto px-4 py-6 space-y-8 safe-area-bottom">
        <section className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Target className="h-6 w-6 text-primary" />
            </div>
            <h2 className="text-2xl font-bold">Sistema de Niveles</h2>
          </div>
          
          <Card>
            <CardContent className="pt-6 space-y-4">
              <p className="text-muted-foreground">
                Tu <strong>nivel global</strong> representa tu habilidad general como jugador. 
                Empieza entre <Badge variant="secondary">1.0</Badge> y <Badge variant="secondary">5.0</Badge>, y puede subir hasta <Badge variant="secondary">7.0</Badge> jugando partidos.
              </p>
              
              <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                <h4 className="font-semibold flex items-center gap-2">
                  <Zap className="h-4 w-4 text-amber-500" />
                  ¿Cómo cambia tu nivel?
                </h4>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <TrendingUp className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                    <span>Ganas puntos al vencer a jugadores de tu nivel o superior</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <TrendingUp className="h-4 w-4 text-red-500 mt-0.5 shrink-0 rotate-180" />
                    <span>Pierdes puntos al perder contra jugadores de tu nivel o inferior</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <BarChart3 className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
                    <span>Los cambios son graduales (~0.06 a 0.12 por partido)</span>
                  </li>
                </ul>
              </div>

              <div className="space-y-3">
                <div className="bg-muted/30 rounded-lg p-4 border border-border">
                  <div className="flex items-start gap-3 mb-2">
                    <Badge className="bg-green-500 shrink-0">1.0 - 1.9</Badge>
                    <h5 className="font-semibold pt-0.5">Iniciación</h5>
                  </div>
                  <p className="text-sm text-muted-foreground">Aprendiendo las reglas y golpes básicos</p>
                </div>
                
                <div className="bg-muted/30 rounded-lg p-4 border border-border">
                  <div className="flex items-start gap-3 mb-2">
                    <Badge className="bg-teal-500 shrink-0">2.0 - 2.9</Badge>
                    <h5 className="font-semibold pt-0.5">Básico</h5>
                  </div>
                  <p className="text-sm text-muted-foreground">Peloteos lentos. Empieza a usar la pared de fondo y a subir a la red.</p>
                </div>
                
                <div className="bg-muted/30 rounded-lg p-4 border border-border">
                  <div className="flex items-start gap-3 mb-2">
                    <Badge className="bg-blue-500 shrink-0">3.0 - 3.9</Badge>
                    <h5 className="font-semibold pt-0.5">Intermedio</h5>
                  </div>
                  <p className="text-sm text-muted-foreground">Juego cruzado y globos. Uso de bandeja y voleas para dominar la red.</p>
                </div>
                
                <div className="bg-muted/30 rounded-lg p-4 border border-border">
                  <div className="flex items-start gap-3 mb-2">
                    <Badge className="bg-violet-500 shrink-0">4.0 - 4.9</Badge>
                    <h5 className="font-semibold pt-0.5">Avanzado</h5>
                  </div>
                  <p className="text-sm text-muted-foreground">Control de paredes dobles. Ritmo alto, pocos errores y remate con intención.</p>
                </div>
                
                <div className="bg-muted/30 rounded-lg p-4 border border-border">
                  <div className="flex items-start gap-3 mb-2">
                    <Badge className="bg-amber-500 shrink-0">5.0</Badge>
                    <h5 className="font-semibold pt-0.5">Muy Avanzado</h5>
                  </div>
                  <p className="text-sm text-muted-foreground">Nivel de competición muy avanzado, en categorías superiores de nivel amateur/semi profesional. Dominio de efectos, potencia y táctica profesional.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        <section className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Trophy className="h-6 w-6 text-primary" />
            </div>
            <h2 className="text-2xl font-bold">Puntuación en Ligas (ELO)</h2>
          </div>
          
          <Card>
            <CardContent className="pt-6 space-y-4">
              <p className="text-muted-foreground">
                Cada liga tiene su propio <strong>sistema de ranking</strong> basado en puntuación ELO. 
                Esta puntuación determina tu posición en la tabla de clasificación de la liga.
              </p>

              <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                <h4 className="font-semibold flex items-center gap-2">
                  <Award className="h-4 w-4 text-amber-500" />
                  Tu Puntuación
                </h4>
                <p className="text-sm text-muted-foreground">
                  La <strong>Puntuación</strong> que ves en tu perfil se calcula automáticamente según tu nivel global actual:
                </p>
                <div className="bg-background rounded p-3 font-mono text-sm text-center">
                  Puntuación = 1000 + (Nivel - 3.0) × 150
                </div>
                <p className="text-sm text-muted-foreground">
                  Cada vez que tu nivel cambia después de un partido, tu puntuación se actualiza automáticamente. 
                  Es una forma rápida de ver tu progreso en una escala de 700 a 1300 puntos.
                </p>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between bg-muted/30 rounded-lg p-3 border border-border">
                  <span className="font-medium">1.0</span>
                  <Badge variant="outline">700</Badge>
                </div>
                <div className="flex items-center justify-between bg-muted/30 rounded-lg p-3 border border-border">
                  <span className="font-medium">2.0</span>
                  <Badge variant="outline">850</Badge>
                </div>
                <div className="flex items-center justify-between bg-muted/30 rounded-lg p-3 border border-border">
                  <span className="font-medium">3.0</span>
                  <Badge variant="outline">1000</Badge>
                </div>
                <div className="flex items-center justify-between bg-muted/30 rounded-lg p-3 border border-border">
                  <span className="font-medium">4.0</span>
                  <Badge variant="outline">1150</Badge>
                </div>
                <div className="flex items-center justify-between bg-muted/30 rounded-lg p-3 border border-border">
                  <span className="font-medium">5.0</span>
                  <Badge variant="outline">1300</Badge>
                </div>
              </div>

              <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                <h4 className="font-semibold flex items-center gap-2">
                  <BarChart3 className="h-4 w-4 text-blue-500" />
                  ¿Cómo funciona el ELO?
                </h4>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 font-bold">+</span>
                    <span>Ganas más puntos al vencer a jugadores con puntuación más alta</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 font-bold">−</span>
                    <span>Pierdes más puntos al perder contra jugadores con puntuación más baja</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-500 font-bold">≈</span>
                    <span>Cambios típicos: entre 10 y 30 puntos por partido</span>
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </section>

        <section className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Users className="h-6 w-6 text-primary" />
            </div>
            <h2 className="text-2xl font-bold">Sistema Dual</h2>
          </div>
          
          <Card>
            <CardContent className="pt-6 space-y-4">
              <p className="text-muted-foreground">
                CourtVibe usa un <strong>sistema dual</strong> que combina tu nivel global con los rankings de liga:
              </p>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <Target className="h-5 w-5 text-primary" />
                    <h4 className="font-semibold">Nivel Global</h4>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Rango: 1.0 - 7.0
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Se actualiza con <strong>todos</strong> tus partidos (liga y sin liga)
                  </p>
                </div>

                <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <Trophy className="h-5 w-5 text-amber-500" />
                    <h4 className="font-semibold">Puntuación de Liga</h4>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Rango: 700 - 1300+ puntos
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Se actualiza <strong>solo</strong> con partidos de esa liga
                  </p>
                </div>
              </div>

              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                <p className="text-sm">
                  <strong>Ejemplo:</strong> Si juegas un partido de liga y ganas, tu nivel global sube 
                  Y tu puntuación en esa liga también sube. Pero si juegas un partido sin liga, 
                  solo tu nivel global cambia.
                </p>
              </div>
            </CardContent>
          </Card>
        </section>

        <section className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Zap className="h-6 w-6 text-primary" />
            </div>
            <h2 className="text-2xl font-bold">Preguntas Frecuentes</h2>
          </div>
          
          <Card>
            <CardContent className="pt-6 space-y-6">
              <div className="space-y-2">
                <h4 className="font-semibold">¿Por qué mi nivel cambia poco?</h4>
                <p className="text-sm text-muted-foreground">
                  El sistema usa cambios graduales para reflejar tu nivel real. 
                  Se necesitan varios partidos para ver cambios significativos, 
                  lo cual evita que una mala racha afecte mucho tu nivel.
                </p>
              </div>

              <div className="space-y-2">
                <h4 className="font-semibold">¿Por qué empecé con diferente puntuación en la liga?</h4>
                <p className="text-sm text-muted-foreground">
                  Tu puntuación inicial de liga se basa en tu nivel global. 
                  Esto hace que los primeros partidos sean más equilibrados 
                  y los rankings reflejen la realidad más rápido.
                </p>
              </div>

              <div className="space-y-2">
                <h4 className="font-semibold">¿Puedo bajar de nivel 1.0?</h4>
                <p className="text-sm text-muted-foreground">
                  No. El nivel mínimo es 1.0 y el máximo es 7.0. 
                  Aunque pierdas muchos partidos, nunca bajarás de 1.0. Niveles por encima de 5.0 solo se alcanzan jugando.
                </p>
              </div>

              <div className="space-y-2">
                <h4 className="font-semibold">¿Por qué necesita confirmación mi partido?</h4>
                <p className="text-sm text-muted-foreground">
                  Para evitar errores o manipulación, el equipo contrario debe confirmar 
                  el resultado. Solo después de la confirmación se actualizan los niveles y puntuaciones.
                </p>
              </div>
            </CardContent>
          </Card>
        </section>

        <div className="pb-8">
          <Button 
            variant="outline" 
            className="w-full"
            onClick={() => setLocation("/")}
            data-testid="button-back-bottom"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver al Inicio
          </Button>
        </div>
      </main>
    </div>
  );
}
